import { apiGet, apiPost } from './client'

/** Contractul tabloului central: GET /api/v2/dashboard si capetele lui. */

export interface DashboardAdviceLine {
  tone: string
  icon: string
  text: string
  url: string
}

export interface DashboardAdvice {
  tone: string
  headline: string
  lines: DashboardAdviceLine[]
}

export interface DashboardCard {
  id: string
  title: string
  module: string
  size: string
  w: number
  h: number
  x?: number | null
  y?: number | null
  html: string
}

export interface DashboardView {
  label: string
  about: string
  icon: string
}

export interface DashboardClass {
  label: string
  about: string
  icon: string
}

export interface DashboardModule {
  label: string
  icon: string
  href: string
}

export interface DashboardMine {
  id: number
  name: string
  based_on: string
  shared: boolean
  default: boolean
  mine: boolean
  owner: string
}

export interface DashboardBoardMeta {
  kind: string
  key: string
  override: number
  edit: boolean
  placed: boolean
  board: { id: number; name: string; based_on: string; shared: boolean; default: boolean; mine: boolean } | null
}

export interface DashboardPage {
  pageTitle: string
  lead: string
  boardUrl: string
  cards: DashboardCard[]
  advice: DashboardAdvice
  tally: { read: number; unreadable: number }
  noCompany: boolean
  history: boolean
  aiOn: boolean
  board: DashboardBoardMeta
  views: Record<string, DashboardView>
  classes: Record<string, DashboardClass>
  windows: Record<string, string>
  modules: Record<string, DashboardModule>
  boards: DashboardMine[]
  scope: DashboardScope
  classic: string
  redirect?: string
}

/**
 * Fereastra tabloului. `from`/`to` sunt perioada aleasa de om, null cand nu e
 * niciuna; `rangeSaid` e motivul pentru care una ceruta n-a fost primita, si se
 * scrie pe ecran langa calendar.
 */
export interface DashboardScope {
  days: number
  class: string
  company: number
  office: number
  from: string | null
  to: string | null
  rangeSaid: string
  maxDays: number
}

export interface DashboardWall {
  title: string
  cards: DashboardCard[]
  tally: { read: number; unreadable: number }
  scope: DashboardScope
  rotate: number
  reload: number
  perPage: number
}

/**
 * Tabloul central: `/dashboard`, sau `/dashboard/board/{id}` cand se cere o
 * tabla anume. Aceeasi pagina si acelasi tip, deci o singura functie - doua
 * lasau ecranul sa aleaga intre doua feluri de cerere, si alegerea aia ii
 * lua tipul paginii cu totul.
 */
export function dashboardPageQuery(boardId: number | undefined, search: string) {
  const q = search.startsWith('?') ? search : search ? `?${search}` : ''
  const path = boardId ? `/dashboard/board/${boardId}${q}` : `/dashboard${q}`
  return {
    queryKey: boardId ? ['dashboard', 'board', boardId, q] : ['dashboard', q],
    queryFn: () => apiGet<DashboardPage>(path),
  }
}

export function dashboardWallQuery(kind: string, key: string, search: string) {
  const q = search.startsWith('?') ? search : search ? `?${search}` : ''
  return {
    queryKey: ['dashboard', 'wall', kind, key, q] as const,
    queryFn: () => apiGet<DashboardWall>(`/dashboard/wall/${kind}/${key}${q}`),
  }
}

export function arrangeBoard(kind: string, key: string, view: string) {
  return apiPost<{ ok: boolean; id: number; message?: string }>('/dashboard/arrange', { kind, key, view })
}

export function createBoard(name: string, basedOn: string) {
  return apiPost<{ ok: boolean; id: number; message?: string }>('/dashboard/boards', { name, based_on: basedOn })
}

export function updateBoard(id: number, what: string, fields: Record<string, string | number> = {}) {
  return apiPost<{ ok: boolean; message?: string }>(`/dashboard/boards/${id}`, { what, ...fields })
}

export function saveBoard(id: number, cards: { card_id: string; size: string; x?: number | null; y?: number | null }[]) {
  return apiPost<{ ok: boolean; cards: number; placed: boolean; message?: string }>(`/dashboard/boards/${id}/cards`, {
    cards: JSON.stringify(cards),
  })
}

export function briefBoard(kind: string, key: string, days: number) {
  return apiGet<{ ok: boolean; question?: string; message?: string }>(`/dashboard/brief/${kind}/${key}?days=${days}`)
}

/* ------------------------------------------------------------------------ *
 * CONSTRUCTORUL - Dashboard::boards / measures / customCard / dropCustomCard
 * ------------------------------------------------------------------------ */

/** Un card asa cum intra in catalogul constructorului (`catalogForBuilder`). */
export interface BuilderCard {
  id: string
  title: string
  question: string
  module: string
  icon: string
  size: string
  /** Caseta proprie a omului - se poate si sterge, de aici. */
  custom: boolean
}

export interface BuilderCatalog {
  boards: DashboardMine[]
  catalog: BuilderCard[]
}

/** O masura din lista inchisa a lui `Measures::$measures`. */
export interface BoardMeasure {
  key: string
  label: string
  question: string
  kind: string
  module: string
  goal: string
  filters: string[]
  /** Formele care se potrivesc felului masurii (`Card::$forms`). */
  forms: string[]
}

export interface BoardMeasures {
  measures: BoardMeasure[]
  /** Numele filtrelor, cu eticheta de pe formular. */
  filters: Record<string, string>
  options: Record<string, { value: string; label: string }[]>
  sizes: string[]
  limit: number
  /** Tabela casetelor proprii a ajuns in baza. */
  ready: boolean
}

export function builderCatalog() {
  return apiGet<BuilderCatalog>('/dashboard/boards')
}

export function boardMeasures() {
  return apiGet<BoardMeasures>('/dashboard/measures')
}

/** Cimpurile formularului "fa-ti o caseta", asa cum le trimitea `odbMaker`. */
export function makeCustomCard(fields: Record<string, string>) {
  return apiPost<{ ok: boolean; card?: BuilderCard; message?: string }>('/dashboard/custom-card', fields)
}

export function dropCustomCard(cardId: string) {
  return apiPost<{ ok: boolean; message?: string }>('/dashboard/custom-card/drop', { card: cardId })
}

/* ------------------------------------------------------------------------ *
 * PANOUL MASINII SI ACTIUNILE DE PE CARD - Dashboard::peek / act
 * ------------------------------------------------------------------------ */

export interface BoardPeekFact {
  label: string
  value: string
  tone: string
}

/** O masina in cateva randuri - `Peek::read`. */
export interface BoardPeek {
  id: number
  name: string
  seen: string
  agent: string
  facts: BoardPeekFact[]
}

export function peekDevice(device: number) {
  return apiGet<BoardPeek>(`/dashboard/peek/${device}`)
}

/** Nimic altceva nu pleaca: ce scrie pe card se citeste din nou pe server. */
export function actOnCard(card: string, action: string, days: number, klass: string) {
  return apiPost<{ ok: boolean; said: string }>('/dashboard/act', { card, action, days, class: klass })
}
